import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useAppSelector } from "../store/hooks";
import { roleLabels } from "../strings/labels";
import { LayoutEvent } from "../components/page-layout/PageLayout/LayoutEvent";
import { LayoutProduct } from "../components/page-layout/PageLayout/LayoutProduct";
import { LayoutMember } from "../components/page-layout/PageLayout/LayoutMember";
import {
  selectEnrichedConnectionsForListing,
  selectListingsWithDetails,
} from "../store/slices/listingsSlice";
import {
  ListingWithDetails,
  EnrichedConnection,
  SubjectOrObject,
  ItemType,
} from "../types";
import axios from "axios";
import {
  selectTabLayoutForListing,
  TabLayoutDict,
} from "../store/slices/uiSlice";
import { AppHeader } from "../components/Header/AppHeader";
import { LayoutNetwork } from "../components/page-layout/PageLayout/LayoutNetwork";

interface ListingDetailPageProps {
  listingId?: string; // Optional prop to override URL param (for custom domains)
}

export const ListingDetailPage: React.FC<ListingDetailPageProps> = ({
  listingId,
}) => {
  // STATE
  const { id: urlId } = useParams<{ id: string }>();
  const id = listingId || urlId; // Use prop if provided, otherwise use URL param
  const [fetchedListing, setFetchedListing] =
    useState<ListingWithDetails | null>(null);
  const [listingLoading, setListingLoading] = useState(false);
  const [listingNotFound, setListingNotFound] = useState(false);

  const reduxListing = useAppSelector(selectListingsWithDetails);
  const locations = useAppSelector((state) => state.locations);
  const enrichedConnections = useAppSelector((state) =>
    selectEnrichedConnectionsForListing(state, id)
  );

  // Example tab layout structure
  // {
  //     "project_dream-collector_4702": [
  //         {
  //             "pageListingId": "member_dan_5513",
  //             "itemListingId": "project_dream-collector_4702",
  //             "tabLabel": "Art"
  //         }
  //     ]
  // }
  const tabLayouts: TabLayoutDict = useAppSelector((state) =>
    selectTabLayoutForListing(state, id || "")
  );

  // Check Redux first, then use fetched listing if Redux doesn't have it
  const localStateListing = id ? reduxListing[id] : null;
  const listing = localStateListing || fetchedListing;

  // VARS
  const numTabLayouts = Object.keys(tabLayouts).length;

  // Organise tabs into something immediately usable by the pages
  const tabs: Record<string, EnrichedConnection[]> = useMemo(() => {
    if (!tabLayouts || !enrichedConnections || !listing) return {};

    const t = enrichedConnections.reduce(
      (acc, curr) => {
        const subjectOrObject: SubjectOrObject = curr.connection.isSubject
          ? "subject"
          : "object";
        const roleType = curr.connection.roleType;
        const listingType: ItemType = listing.itemType;
        const defaultLabel =
          roleLabels[subjectOrObject][listingType][roleType] || "Error";
        const customTabs = tabLayouts[curr.listing.id];
        const customLabels: string[] = customTabs
          ? customTabs.map((x) => x.tabLabel)
          : [];

        if (customLabels.length > 0) {
          return customLabels.reduce((acc2, label) => {
            return {
              ...acc2,
              [label]: [...(acc2[label] || []), curr],
            };
          }, acc);
        } else {
          return {
            ...acc,
            [defaultLabel]: [...(acc[defaultLabel] || []), curr],
          };
        }
      },
      {} as Record<string, EnrichedConnection[]>
    );

    return t;
  }, [listing?.id, numTabLayouts, enrichedConnections?.length]);

  // Fetch listing from API if not in Redux
  useEffect(() => {
    const fetchListing = async () => {
      if (!id) return;

      // If Redux already has the listing, no need to fetch
      if (localStateListing) {
        setFetchedListing(null); // Clear any previously fetched data
        return;
      }

      try {
        setListingLoading(true);
        setListingNotFound(false);
        console.log(`Fetching listing ${id} from API (not in Redux)`);
        const response = await axios.get(`/api/listings/${id}`);
        setFetchedListing(response.data);
      } catch (error: any) {
        console.error("Error fetching listing:", error);
        if (error.response?.status === 404) {
          setListingNotFound(true);
        }
      } finally {
        setListingLoading(false);
      }
    };

    fetchListing();
  }, [id, localStateListing]);

  // Show loading state while fetching listing
  if (listingLoading) {
    return (
      <div className="detail-page">
        <AppHeader
          title={"Loading..."}
          hasBackButton={true}
          setShowJoinForm={() => {}}
          iconUrl={null}
          listingId={id || ""}
        />
      </div>
    );
  }

  // Show not found state
  if (listingNotFound || (!listing && !listingLoading)) {
    return (
      <div className="detail-page">
        <AppHeader
          title={"Error"}
          hasBackButton={true}
          setShowJoinForm={() => {}}
          iconUrl={null}
          listingId={id || ""}
        />
        <div className="detail-content">
          <h1>Listing not found</h1>
          <p>
            The listing you're looking for doesn't exist or has been removed.
          </p>
        </div>
      </div>
    );
  }

  // TypeScript guard: member is guaranteed to exist here
  if (!listing) return null;

  const memberLocation = listing.locations?.[0];
  const location = memberLocation
    ? locations.byId[memberLocation.locationId]
    : null;

  // Render appropriate layout based on item type
  const renderLayout = () => {
    if (listing.itemType === "organization") {
      return (
        <LayoutNetwork
          listing={listing}
          location={location}
          enrichedConnections={enrichedConnections}
          tabs={tabs}
        />
      );
    }

    if (listing.itemType === "event") {
      return (
        <LayoutEvent
          listing={listing}
          location={location}
          enrichedConnections={enrichedConnections}
          tabs={tabs}
        />
      );
    }

    if (listing.itemType === "product") {
      return (
        <LayoutProduct
          listing={listing}
          location={location}
          enrichedConnections={enrichedConnections}
          tabs={tabs}
        />
      );
    }

    // Default to member layout
    return (
      <LayoutMember
        listing={listing}
        location={location}
        enrichedConnections={enrichedConnections}
        hasContactForm={true}
        tabs={tabs}
      />
    );
  };

  return (
    <div className="detail-page">
      <AppHeader
        title={listing.name}
        hasBackButton={true}
        setShowJoinForm={() => {}}
        iconUrl={null}
        listingId={id || ""}
      />
      {renderLayout()}
    </div>
  );
};
